// Field-level stage_timeline writes.
//
// The Timelines page builds every save from the copy of stage_timeline it loaded. Writing that
// whole copy back puts stale values over anything another page saved in the meantime; most
// visibly the Tracking page's Planned / Contract / Actual dates, which live on the same stage
// entries. applyTimelineDiff works out what the caller actually changed (base -> next) and applies
// only that onto a freshly read copy, so fields the caller never touched keep their current value.

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// Order-insensitive deep equality. JSON from Postgres jsonb does not keep key order, so a
// JSON.stringify comparison would report untouched entries as changed.
export function deepEqual(a, b) {
  if (a === b) return true;
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i])) return false;
    return true;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    const ka = Object.keys(a).filter(k => a[k] !== undefined);
    const kb = Object.keys(b).filter(k => b[k] !== undefined);
    if (ka.length !== kb.length) return false;
    for (const k of ka) if (!deepEqual(a[k], b[k])) return false;
    return true;
  }
  return false;
}

/**
 * Apply the change base -> next onto fresh.
 *  - top-level keys unchanged between base and next keep fresh's value
 *  - a key removed in next is removed
 *  - an object entry present in base, next and fresh is patched field by field
 *  - anything else (new keys, arrays, scalars) takes next's value
 */
export function applyTimelineDiff(base, next, fresh) {
  const b = base || {};
  const n = next || {};
  const out = { ...(fresh || {}) };
  const keys = new Set([...Object.keys(b), ...Object.keys(n)]);
  for (const k of keys) {
    const bv = b[k];
    const nv = n[k];
    if (deepEqual(bv, nv)) continue;
    if (nv === undefined) { delete out[k]; continue; }
    if (isPlainObject(bv) && isPlainObject(nv) && isPlainObject(out[k])) {
      const merged = { ...out[k] };
      const fields = new Set([...Object.keys(bv), ...Object.keys(nv)]);
      for (const f of fields) {
        if (deepEqual(bv[f], nv[f])) continue;
        if (nv[f] === undefined) delete merged[f];
        else merged[f] = nv[f];
      }
      out[k] = merged;
    } else {
      out[k] = nv;
    }
  }
  return out;
}

// Fields owned by the Tracking page (and the Invoices page), on a stage entry and on each of its
// partStages. The Timelines page may only change these while "Sync to tracking" is on.
export const TRACKING_FIELDS = [
  'contractDate', 'targetDate', 'actualDate', 'clientApprovalDate', 'proformaInvoiceDate',
  'invoiceDate', 'paidDate', 'invoicePercent', 'billing', 'emailStatus', '_syncLocked',
  // CC retainer lifecycle, stored on stage_timeline.cc
  'startDate', 'stopDate', 'pausedPeriods', 'invoices',
];

function hasTracking(v) {
  return isPlainObject(v) && (TRACKING_FIELDS.some(f => v[f] !== undefined) || Array.isArray(v.partStages));
}

// Copy the Tracking fields of `fresh` onto `entry`: fresh values win, and a Tracking field fresh
// does not have is removed, so nothing can be added either.
function withFreshTracking(entry, fresh) {
  const out = { ...entry };
  for (const f of TRACKING_FIELDS) {
    if (isPlainObject(fresh) && fresh[f] !== undefined) out[f] = fresh[f];
    else delete out[f];
  }
  return out;
}

function protectParts(nextParts, freshParts) {
  const fresh = Array.isArray(freshParts) ? freshParts : [];
  if (!Array.isArray(nextParts)) return fresh.length ? fresh : nextParts;
  const freshById = new Map(fresh.map(p => [p?.id, p]));
  const out = nextParts.map(p => (isPlainObject(p) ? withFreshTracking(p, freshById.get(p.id)) : p));
  // Parts are created and removed on the Tracking page, never here: one missing from next comes back.
  const kept = new Set(nextParts.map(p => p?.id));
  fresh.forEach(p => { if (!kept.has(p?.id)) out.push(p); });
  return out;
}

/**
 * Undo any change `next` makes to Tracking-owned fields relative to `fresh` (the stored timeline).
 * Used when "Sync to tracking" is off: planning edits, resets and restores still apply, but every
 * stage and part keeps the Contract / Planned / Actual / invoice / paid / billing values it has now.
 */
export function protectTrackingFields(next, fresh) {
  const n = next || {};
  const f = fresh || {};
  const out = { ...n };
  const keys = new Set([...Object.keys(n), ...Object.keys(f)]);
  for (const k of keys) {
    if (k.startsWith('_')) continue; // metadata (_rows, _viewSettings, ...)
    const nv = n[k];
    const fv = f[k];
    if (!hasTracking(nv) && !hasTracking(fv)) continue;
    const base = isPlainObject(nv) ? nv : {};
    const guarded = withFreshTracking(base, fv);
    const parts = protectParts(base.partStages, isPlainObject(fv) ? fv.partStages : undefined);
    if (parts === undefined) delete guarded.partStages; else guarded.partStages = parts;
    if (!isPlainObject(nv) && Object.keys(guarded).length === 0) continue;
    out[k] = guarded;
  }
  return out;
}
